// src/features/consultation/TriageAllergyChip.tsx
// The Consultation-only "triage allergy, not in record" review chip —
// docs/HMS_V4_CONSULTATION_REDESIGN_PLAN.md Phase 2. Rendered by
// VisitWorkflow.tsx into PatientBanner's `extra` slot (gated to the
// consultation stage there), never mounted by PatientBanner itself — the
// banner stays stage-agnostic, this is what a stage-specific extra is for.
import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { amendVisitStage } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { AllergyPanel } from './AllergyPanel';
import { useAllergies } from './useAllergies';
import { needsTriageReview, type TriagePreConsultAllergyFields } from './triageAllergyReconciliation';

// Fixed, descriptive reason for the amend audit trail — logAudit already
// records who/when via the verified JWT, so a one-click dismissal doesn't
// need to make the doctor type a reason for something this low-stakes and
// reversible (re-adding the allergy just makes the chip stop matching again).
const DISMISS_REASON = 'Reviewed nurse-reported allergy text at Consultation — not a genuine allergy';

export interface TriageAllergyChipProps {
  uhid: string;
  /** Business-key visit id (VIS-...) — what the amend endpoint addresses. */
  visitId: string;
  preConsult: TriagePreConsultAllergyFields | null;
  /** Pushes the freshly amended visit back into VisitWorkflow's own state,
   * same convention StageEditModal's onVisitUpdated already uses. */
  onReconciled: (visit: any) => void;
}

export function TriageAllergyChip({ uhid, visitId, preConsult, onReconciled }: TriageAllergyChipProps) {
  const { user } = useAuth();
  const allergiesQuery = useAllergies(uhid);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState('');

  const text = needsTriageReview(
    preConsult,
    allergiesQuery.data,
    !allergiesQuery.isLoading && !allergiesQuery.isError
  );
  if (!text) return null;

  const dismiss = async () => {
    setDismissing(true); setError('');
    try {
      const updated = await amendVisitStage(visitId, 'pre_consult', {
        allergies_reconciled: true,
        allergies_reconciled_by: user?.full_name || user?.username,
        allergies_reconciled_at: new Date().toISOString(),
      }, DISMISS_REASON);
      onReconciled(updated);
      setReviewOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDismissing(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1.5 rounded-md border border-dashed border-warn bg-warn-soft px-2.5 py-1 text-[12px] text-warn-ink">
        <span>{text} — triage only, not in record</span>
        <Popover.Root open={reviewOpen} onOpenChange={setReviewOpen}>
          <Popover.Trigger asChild>
            <button type="button" className="font-semibold underline-offset-2 hover:underline">
              Review
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align="end"
              sideOffset={6}
              className="z-[150] w-72 rounded-[var(--radius)] border border-line bg-card p-3 shadow-lg"
            >
              <div className="flex flex-col gap-2.5">
                <div className="text-[12.5px] text-ink">
                  Triage said <b>&ldquo;{text}&rdquo;</b> — not found in this patient&apos;s allergy record.
                </div>
                {error && <div className="text-[11px] text-danger">{error}</div>}
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => { setAddOpen(true); setReviewOpen(false); }}>
                    Add to record…
                  </Button>
                  <Button size="sm" variant="ghost" loading={dismissing} onClick={dismiss}>
                    Not an allergy
                  </Button>
                </div>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add allergy</DialogTitle>
          </DialogHeader>
          <AllergyPanel uhid={uhid} preConsultAllergiesText={text} />
        </DialogContent>
      </Dialog>
    </>
  );
}
